import {
  Chip,
  IconButton,
  makeStyles,
  TableBody,
  TableCell,
  TableFooter,
  TableRow,
  Tooltip
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { CouponCollectionListQuery_couponCollections_edges_node } from "@saleor/coupons/types/CouponCollectionListQuery";
import { SubmitPromise } from "@saleor/hooks/useForm";
import { maybe, renderCollection } from "@saleor/misc";
import { PageListProps } from "@saleor/types";
import React, { CSSProperties } from "react";
import { FormattedMessage } from "react-intl";
import Moment from "react-moment";

import CouponFeaturedStar from "../CouponFeaturedStar";
import {
  CouponStatus,
  fiberIconColors,
  statusColors
} from "../CouponRankingList/style";

const useStyles = makeStyles(
  theme => {
    const overflowing: CSSProperties = {
      overflow: "hidden",
      textOverflow: "ellipsis"
    };
    return {
      categoryChip: {
        background: "none",
        border: "3px solid",
        margin: "3px"
      },
      categoryTable: {
        width: "100%"
      },
      colActions: {
        alignItems: "center",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-around",
        padding: 0
      },
      colActionsDiv: {
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 10
      },
      colDate: {
        ...overflowing,
        width: 280,
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colFeatured: {
        fontWeight: "bold",
        width: 140
      },
      colHeader: {
        fontWeight: "bold"
      },
      colMedium: {
        overflowing,
        width: "12em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colNarrow: {
        overflowing,
        width: 240,
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colRow: {
        "& th": {
          fontWeight: 600 as 600,
          paddingBottom: theme.spacing(1),
          paddingTop: theme.spacing(1)
        }
      },
      colWide: {
        width: "13em"
      },
      colWideHeaderCode: {
        fontWeight: "bold",
        overflowing,
        width: "15em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colWideHeaderMall: {
        fontWeight: "bold",
        overflowing,
        width: "8em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colWideHeaderQty: {
        fontWeight: "bold",
        overflowing,
        width: "8em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colWideIcon: {
        overflowing,
        width: "100px",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      couponCellCode: {
        backgroundColor: "#B4F2DF",
        borderRadius: "5px",
        color: "#2C7269",
        display: "inline-block",
        fontWeight: "bold",
        padding: "6px",
        textAlign: "center",
        width: "auto !important"
      },
      couponCellRedeemStatus: {
        color: "#000000",
        fontWeight: "bold"
      },
      couponDates: {
        fontSize: "12px"
      },
      createCouponButton: {
        backgroundColor: "#1848FE"
      },
      featuredStar: {
        marginLeft: "20px"
      },
      filters: {
        "& > *": {
          marginRight: theme.spacing(2)
        },
        alignItems: "center",
        display: "flex",
        padding: theme.spacing(2)
      },
      iconStatusCompleted: {
        color: "#63D5B6"
      },
      iconStatusEditing: {
        color: "#FECE21"
      },
      iconStatusOngoing: {
        color: "#5D7FFE"
      },
      redeemStatus: {
        fontSize: "14px"
      },
      redeemSubText: {
        fontSize: "10px"
      },
      statusCompleted: {
        backgroundColor: "#C2F2E9",
        borderRadius: "30px",
        display: "inline-block",
        padding: "3px"
      },
      statusEditing: {
        backgroundColor: "#FEF0BD",
        borderRadius: "30px",
        display: "inline-block",
        padding: "3px"
      },
      statusOngoing: {
        backgroundColor: "#BAC8FF",
        borderRadius: "30px",
        display: "inline-block",
        padding: "3px"
      },
      table: {
        tableLayout: "fixed"
      },
      tableCodes: {
        backgroundColor: "#B4F3DF",
        borderRadius: "5px",
        color: "#236C63",
        fontWeight: "bold",
        overflow: "hidden",
        padding: "6px",
        textAlign: "left",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        width: "fit-content"
      },
      tableRow: {
        "& td": {
          wordBreak: "break-all"
        }
      },
      tableStatuses: {
        fontWeight: "bold"
      },
      tableTruncate: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      },
      viewMallCell: {
        "&:hover": {
          color: "#0030FF",
          textDecoration: "underline"
        },
        color: "#247CFF",
        cursor: "pointer"
      }
    };
  },
  { name: "CollectionList" }
);

interface CouponCollectionTableProps extends PageListProps {
  collections?: CouponCollectionListQuery_couponCollections_edges_node[];
  userHasFeatureCouponAccess: boolean;
  onSubmit?: (data) => SubmitPromise;
}

const CouponCollectionTable: React.FC<CouponCollectionTableProps> = props => {
  const {
    collections,
    settings,
    onRowClick,
    pageInfo,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    userHasFeatureCouponAccess,
    disabled,
    onSubmit
  } = props;
  const classes = useStyles(props);
  const NUMBER_OF_COLUMNS = 5;

  const categoryColors = {
    CYBERZONE: "#7BDD85",
    DINING: "#FA9E4A",
    ENTERTAINMENT: "#3C5BCD",
    GROCERY: "#247CFF",
    SHOPPING: "#EC5D77",
    WELLNESS: "#B896FF"
  };

  const categoryList = [
    "CYBERZONE",
    "DINING",
    "ENTERTAINMENT",
    "GROCERY",
    "SHOPPING",
    "WELLNESS",
    "EVENTS"
  ];

  return (
    <ResponsiveTable>
      <TableHead colSpan={NUMBER_OF_COLUMNS} items={[]} disabled={false}>
        {userHasFeatureCouponAccess && (
          <TableCellHeader className={classes.colFeatured}>
            <FormattedMessage defaultMessage="Featured" />
          </TableCellHeader>
        )}
        <TableCellHeader
          className={`${classes.colNarrow} ${classes.colHeader}`}
        >
          <FormattedMessage defaultMessage="Collection Title" />
        </TableCellHeader>
        <TableCellHeader
          className={`${classes.colNarrow} ${classes.colHeader}`}
        >
          <FormattedMessage defaultMessage="Collection Status" />
        </TableCellHeader>
        <TableCellHeader className={`${classes.colDate} ${classes.colHeader}`}>
          <FormattedMessage defaultMessage="Availability Date(s)" />
        </TableCellHeader>
        <TableCellHeader className={classes.colHeader}>
          <FormattedMessage defaultMessage="Assigned Categories" />
        </TableCellHeader>
        <TableCellHeader className={classes.colWideIcon}></TableCellHeader>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={NUMBER_OF_COLUMNS}
            settings={settings}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {renderCollection(
          collections,
          (collection, index) => {
            const renderCategory =
              collection?.categories?.length === 0
                ? categoryList
                : collection?.categories;

            return (
              <TableRow
                className={classes.tableRow}
                hover={!!collection}
                key={collection?.id}
                onClick={undefined}
              >
                {userHasFeatureCouponAccess && (
                  <TableCell className={classes.colFeatured}>
                    <div onClick={e => e.stopPropagation()}>
                      <CouponFeaturedStar
                        id={collection?.id}
                        isFeatured={collection.isFeatured}
                        onSubmit={onSubmit}
                      />
                    </div>
                  </TableCell>
                )}
                <TableCell
                  className={classes.colNarrow}
                  data-test="collectionTitle"
                  id={`collectionTitle${index}`}
                >
                  <Tooltip title={collection?.title}>
                    <div className={classes.tableTruncate}>
                      {collection?.title.length > 15
                        ? `${collection?.title.slice(0, 15)}...`
                        : collection?.title}
                    </div>
                  </Tooltip>
                </TableCell>
                <TableCell
                  className={classes.colNarrow}
                  data-test="collectionStatus"
                  id={`collectionStatus${index}`}
                >
                  <div className={classes.tableStatuses}>
                    {maybe(() => (
                      <Chip
                        label={CouponStatus[collection?.status]}
                        style={{
                          backgroundColor: statusColors[collection?.status]
                        }}
                        icon={
                          <FiberManualRecordIcon
                            style={{
                              color: fiberIconColors[collection?.status]
                            }}
                          />
                        }
                      />
                    ))}
                  </div>
                </TableCell>
                <TableCell
                  className={`${classes.colDate} ${classes.couponDates}`}
                  data-test="collectionDates"
                  id={`collectionDates${index}`}
                >
                  {collection?.startDate && collection?.endDate ? (
                    <>
                      Starts on{" "}
                      <strong>
                        {
                          <Moment format="MM/DD/YYYY" tz="Asia/Manila">
                            {collection?.startDate}
                          </Moment>
                        }
                      </strong>{" "}
                      Until{" "}
                      <strong>
                        {
                          <Moment format="MM/DD/YYYY" tz="Asia/Manila">
                            {collection?.endDate}
                          </Moment>
                        }
                      </strong>{" "}
                    </>
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colWide}
                  data-test="collectionCategories"
                  id={`collectionCategories${index}`}
                >
                  <div className={classes.categoryTable}>
                    {renderCategory.map(item => {
                      const category = item.toLocaleUpperCase();
                      return (
                        <Chip
                          className={classes.categoryChip}
                          id={`${category}Chip${index}`}
                          label={category}
                          key={category}
                          style={{
                            borderColor: categoryColors[category],
                            color: categoryColors[category]
                          }}
                        />
                      );
                    })}
                  </div>
                </TableCell>
                <TableCell className={classes.colActionsDiv}>
                  <div className={classes.colActions}>
                    <IconButton
                      onClick={onRowClick(collection.id)}
                      id={`collectionEditBtn${index}`}
                    >
                      <EditIcon
                        style={{
                          color:
                            collection.status !== "EXPIRED"
                              ? "#0000FF"
                              : "#F1F2F3"
                        }}
                      />
                    </IconButton>
                  </div>
                </TableCell>
              </TableRow>
            );
          },
          () => (
            <TableRow>
              <TableCell colSpan={NUMBER_OF_COLUMNS}>
                <FormattedMessage defaultMessage="No coupon collections found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};
export default CouponCollectionTable;
